import { createFileRoute, Link } from "@tanstack/react-router";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  Beer, Calendar, MapPin, Mic, Music, Phone, Trophy, UtensilsCrossed,
  Star, Clock, ArrowRight, Instagram, Facebook, Menu, X,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useReservationModal } from "@/context/ReservationContext";
import type { BookingType } from "@/components/reservations/ReservationModal";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listPublishedEvents } from "@/lib/events.functions";
import { MENU } from "@/data/menu";
import { listPublishedMenu } from "@/lib/menu.functions";
import { mergeMenu } from "@/lib/menu-merge";
import { trackEvent } from "@/lib/analytics";
import cardKaraoke from "@/assets/card-karaoke.jpg";
import karaokeHomeWideAsset from "@/assets/karaoke-36.webp.asset.json";
import karaokeHomeStageAsset from "@/assets/karaoke-37.webp.asset.json";
import karaokeHomeScreenAsset from "@/assets/karaoke-38.webp.asset.json";
import karaokeStageNeonHomeAsset from "@/assets/karaoke-35.webp.asset.json";
import cardMusic from "@/assets/card-music.jpg";


import heroBannerAsset from "@/assets/hero-banner.png.asset.json";
import heroInteriorAsset from "@/assets/hero-wall-of-games.png.asset.json";
const heroExterior = heroBannerAsset.url;
const heroInterior = heroInteriorAsset.url;
const karaokeHomeWide = karaokeHomeWideAsset.url;
const karaokeHomeStage = karaokeHomeStageAsset.url;
const karaokeHomeScreen = karaokeHomeScreenAsset.url;
const karaokeStageNeonHome = karaokeStageNeonHomeAsset.url;
import cardFood from "@/assets/card-food.jpg";
import cardMatchday from "@/assets/card-matchday.jpg";
import cardPool from "@/assets/card-pool.jpg";
import groupTable from "@/assets/group-table.jpg";
import logoHorizontal from "@/assets/plunkets-horizontal.png.asset.json";
import { SiteFooter as Footer } from "@/components/SiteFooter";
import venuePoolStained from "@/assets/venue-pool-stained-glass.png.asset.json";
import venuePoolRafters from "@/assets/venue-pool-rafters.png.asset.json";
import venueDartsAction from "@/assets/venue-darts-action.png.asset.json";
import venuePoolAction from "@/assets/venue-pool-action.png.asset.json";

import tonightParallaxBg from "@/assets/tonight-parallax-bg.png.asset.json";
import groupNightsParallaxBg from "@/assets/group-nights-parallax.png.asset.json";
import poolBookingParallaxBg from "@/assets/pool-booking-parallax.png.asset.json";
const galleryImages = [
  { src: venuePoolStained.url, alt: "Upstairs pool tables under stained-glass windows at Plunkets Pub Dublin city centre", caption: "Upstairs pool room" },
  { src: venuePoolRafters.url, alt: "Wall of Games pool and darts room with timber rafters at Plunkets Dublin pub", caption: "The Wall of Games" },
  { src: venueDartsAction.url, alt: "Darts match at the tournament-grade oche inside Plunkets Pub Dublin", caption: "Darts oche in action" },
  { src: venuePoolAction.url, alt: "Pool match night beside the mural at Plunkets Dublin city centre pub", caption: "Match night at the tables" },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Plunkets — Sport Pub, Pool Tables & Cocktail Bar in Dublin City Centre" },
      { name: "description", content: "Plunkets is the sport pub, pool table venue and cocktail bar in Dublin city centre. Live Premier League, GAA, hurling, World Cup football & international rugby on 12 large TVs, 3 pool tables, 3 dart boards, karaoke terrace and 2 cocktails for €18 Mon–Thu on Middle Abbey Street, Dublin 1." },
      { name: "keywords", content: "sport pub Dublin, sports pub Dublin city centre, pool table Dublin, pool tables Dublin city centre, cocktail bar Dublin, cocktail pub Dublin city centre, karaoke Dublin, live sport Dublin, Middle Abbey Street pub" },
      { property: "og:title", content: "Plunkets — Sport Pub, Pool Tables & Cocktail Bar in Dublin City Centre" },
      { property: "og:description", content: "Live sport on 12 large TVs, 3 pool tables, 3 dart boards, cocktails, karaoke terrace and live music on Middle Abbey Street, Dublin 1." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://plunkets.ie/" },
      { property: "og:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { property: "og:image:secure_url", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:alt", content: "Plunkets Pub Dublin — sport, pool tables, cocktails and karaoke on Middle Abbey Street" },
      { property: "og:locale", content: "en_IE" },
      { property: "og:site_name", content: "Plunkets Pub Dublin" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@plunketspub" },
      { name: "twitter:title", content: "Plunkets — Sport Pub, Pool Tables & Cocktail Bar in Dublin City Centre" },
      { name: "twitter:description", content: "Live sport on 12 large TVs, 3 pool tables, 3 dart boards, cocktails, karaoke terrace and live music on Middle Abbey Street, Dublin 1." },
      { name: "twitter:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { name: "twitter:image:alt", content: "Plunkets Pub Dublin" },
    ],
    links: [
      { rel: "canonical", href: "https://plunkets.ie/" },
      // Preload the LCP hero image so it starts downloading with the HTML
      // instead of after JS parses and mounts the hero component.
      {
        rel: "preload",
        as: "image" as unknown as undefined,
        href: heroBannerAsset.url,
        fetchPriority: "high" as unknown as undefined,
      } as never,
    ],
    scripts: [
      {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BarOrPub",
        "@id": "https://plunkets.ie/#business",
        name: "Plunkets Pub",
        description: "Sport pub, pool tables and cocktail bar in Dublin city centre.",
        url: "https://plunkets.ie/",
        telephone: "+353-1-000-0000",
        priceRange: "€€",
        image: "https://plunkets.ie/og-default.jpg?v=3",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Middle Abbey Street",
          addressLocality: "Dublin",
          addressRegion: "Dublin 1",
          postalCode: "D01",
          addressCountry: "IE",
        },
        geo: { "@type": "GeoCoordinates", latitude: 53.3487, longitude: -6.2607 },
        servesCuisine: ["Pub food", "Cocktails"],
        hasMenu: "https://plunkets.ie/cocktails-menu",
        sameAs: [
          "https://www.instagram.com/plunketspub",
          "https://www.facebook.com/plunketspub",
        ],
        openingHoursSpecification: [
          { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Sunday"], opens: "12:00", closes: "23:30" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: ["Friday","Saturday"], opens: "12:00", closes: "00:30" },
        ],
        amenityFeature: [
          { "@type": "LocationFeatureSpecification", name: "Large TVs for live sport", value: 12 },
          { "@type": "LocationFeatureSpecification", name: "Pool tables", value: 3 },
          { "@type": "LocationFeatureSpecification", name: "Dart boards", value: 3 },
          { "@type": "LocationFeatureSpecification", name: "Karaoke terrace", value: true },
          { "@type": "LocationFeatureSpecification", name: "Cocktail bar", value: true },
          { "@type": "LocationFeatureSpecification", name: "Live music (Thu–Sun)", value: true },
          { "@type": "LocationFeatureSpecification", name: "Group & private hire", value: true },
        ],
        makesOffer: [
          { "@type": "Offer", name: "Pool table hire", price: "12", priceCurrency: "EUR", priceSpecification: { "@type": "UnitPriceSpecification", price: "12", priceCurrency: "EUR", unitCode: "HUR" } },
          { "@type": "Offer", name: "Dart board hire", price: "12", priceCurrency: "EUR", priceSpecification: { "@type": "UnitPriceSpecification", price: "12", priceCurrency: "EUR", unitCode: "HUR" } },
          { "@type": "Offer", name: "2 cocktails for €18 (Mon–Thu)", priceCurrency: "EUR" },
        ],
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://plunkets.ie/#website",
        url: "https://plunkets.ie/",
        name: "Plunkets Pub Dublin",
        publisher: { "@id": "https://plunkets.ie/#business" },
        inLanguage: "en-IE",
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "How many TVs does Plunkets have for live sport?",
            acceptedAnswer: { "@type": "Answer", text: "Plunkets shows live sport on 12 large TVs across the pub, covering Premier League, GAA, hurling, World Cup football and international rugby." } },
          { "@type": "Question", name: "How many pool tables and dart boards are at Plunkets?",
            acceptedAnswer: { "@type": "Answer", text: "There are 3 pool tables and 3 dart boards. Pool is €12 per hour and darts is €15 per hour; you can walk in or book online." } },
          { "@type": "Question", name: "Where is Plunkets Pub in Dublin?",
            acceptedAnswer: { "@type": "Answer", text: "Plunkets is on Middle Abbey Street, Dublin 1 — in Dublin city centre, minutes from O'Connell Street and the Luas." } },
          { "@type": "Question", name: "What time does Plunkets close?",
            acceptedAnswer: { "@type": "Answer", text: "Plunkets is open until 23:30 daily, with Friday and Saturday running until 00:30. Live music Thursday to Sunday." } },
          { "@type": "Question", name: "Are there any cocktail deals?",
            acceptedAnswer: { "@type": "Answer", text: "Yes — 2 cocktails for €18 runs Monday to Thursday on any cocktail from the menu." } },
        ],
      }),
    }],
  }),
  component: Home,
});

function Home() {
  // Redirect legacy #karaoke hash links to the dedicated /karaoke-dublin page
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#karaoke") {
      history.pushState(null, "", window.location.pathname + window.location.search);
      window.location.replace("/karaoke-dublin");
    }
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-clip bg-background text-foreground">
      <Nav />

      <Hero />
      <WhyPlunkets />
      <PoolDartsBooking />
      <MenuPreview />
      <TonightEvents />
      <KaraokeLounge />
      <GroupBooking />
      <Reviews />
      <Location />
      <Footer />
    </div>
  );
}

/* ---------- NAV ---------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const { open: openModal } = useReservationModal();
  const links: { to?: string; href?: string; label: string }[] = [
    { to: "/pooltable-dublin-citycenter", label: "Pool Tables" },
    { to: "/food-menu", label: "Food Menu" },
    { to: "/cocktails", label: "Cocktails" },
    { to: "/karaoke-dublin", label: "Karaoke Terrace" },
    { to: "/events", label: "What's On" },
    { href: "#find", label: "Find Us" },
  ];
  return (
    <header className="glass-nav fixed top-0 z-50 w-full">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
        <Link to="/" aria-label="Plunkets — Live Music & Sports" className="flex items-center">
          <img src={logoHorizontal.url} alt="Plunkets — Live Music & Sports" width={220} height={56} className="h-9 w-auto md:h-10" decoding="async" />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} className="text-sm font-medium text-muted-foreground transition hover:text-primary">
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href} className="text-sm font-medium text-muted-foreground transition hover:text-primary">
                {l.label}
              </a>
            )
          )}
        </nav>

        <button onClick={() => openModal({ bookingType: "food_and_drinks" })} className="hidden rounded-full btn-amber px-5 py-2 text-sm font-semibold md:inline-block">
          Book a Table
        </button>
        <button className="md:hidden text-foreground" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-background/95 px-5 py-4 md:hidden">
          <div className="flex flex-col gap-3">
            {links.map((l) =>
              l.to ? (
                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-base text-muted-foreground">
                  {l.label}
                </Link>
              ) : (
                <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="text-base text-muted-foreground">
                  {l.label}
                </a>
              )
            )}

            <button onClick={() => { setOpen(false); openModal({ bookingType: "food_and_drinks" }); }} className="mt-2 rounded-full btn-amber px-5 py-2.5 text-center text-sm font-semibold">
              Book a Table
            </button>
          </div>
        </div>
      )}
    </header>
  );
}


/* ---------- HERO ---------- */
function Hero() {
  const { open: openModal } = useReservationModal();

  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const lite = reduce || isMobile;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yExterior = useTransform(scrollYProgress, [0, 1], ["0%", lite ? "0%" : "30%"]);
  const opacityExt = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const opacityInt = useTransform(scrollYProgress, [0.1, 0.7], [0, 1]);
  const scaleInt = useTransform(scrollYProgress, [0.1, 0.9], [1.1, lite ? 1.1 : 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", lite ? "0%" : "60%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] w-full grain overflow-hidden">
      {/* Exterior layer */}
      <motion.div className="absolute inset-0" style={{ y: yExterior, opacity: opacityExt }}>
        <img src={heroExterior} alt="Plunkets Pub exterior on Middle Abbey Street at night" className="h-full w-full object-cover animate-kenburns" width={1920} height={1280} fetchPriority="high" decoding="async" sizes="100vw" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/30 to-background" />
      </motion.div>
      {/* Interior layer — skipped on mobile to avoid a second full-screen decode */}
      {!isMobile && (
        <motion.div className="absolute inset-0" style={{ opacity: opacityInt, scale: scaleInt }}>
          <img src={heroInterior} alt="Inside Plunkets Pub" className="h-full w-full object-cover" width={1920} height={1280} loading="lazy" decoding="async" sizes="100vw" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background" />
        </motion.div>
      )}

      {/* Light flares */}
      <div className="pointer-events-none absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-primary/20 blur-3xl animate-shimmer" />
      <div className="pointer-events-none absolute bottom-0 right-10 h-72 w-72 rounded-full bg-secondary/40 blur-3xl animate-shimmer" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 flex h-full max-w-7xl mx-auto items-center px-5 md:px-8">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> Middle Abbey Street · Dublin 1
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="font-display text-[2.5rem] font-bold leading-[1] tracking-tight sm:text-5xl md:text-7xl lg:text-[5.5rem]">
            Sports Bar, Live Music,<br />
            <span className="text-gold-gradient">Pool &amp; Darts, Karaoke &amp; Cocktails</span><br />
            in Dublin City Centre
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
            Pool tables, Karaoke Terrace, live sport on every screen and proper cocktails — all under one roof. Walk in or book ahead.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
            className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => openModal({ bookingType: "food_and_drinks" })} className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
              Book a Table <ArrowRight size={16} />
            </button>

            <a href="#tonight" className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
              See What's On Tonight
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 animate-float-slow">
        <div className="flex flex-col items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
          Scroll
          <span className="h-10 w-px bg-gradient-to-b from-primary to-transparent" />
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 1: Why Plunkets (3 feature cards + 4 stats) ---------- */
const featureCards = [
  {
    badge: "3 POOL · 3 DARTS",
    badgeColor: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    title: "3 Pool Tables & 3 Dart Boards",
    desc: "Three well-maintained pool tables and three tournament-grade dart boards. Walk in or book — never a long wait on a week night. Pool €12/hr · darts €15/hr.",
    img: cardPool,
    alt: "Pool tables and dart boards at Plunkets Pub Dublin city centre",
    href: "/pooltable-dublin-citycenter",
  },
  {
    badge: "12 LARGE TVS",
    badgeColor: "bg-sky-500/20 text-sky-300 border-sky-500/30",
    title: "Every Match, Every Screen",
    desc: "12 large TVs showing Premier League, GAA, hurling, World Cup football, international rugby, UFC and F1 — plus retro/old GAA & hurling classics daily. Proper sound. Proper atmosphere.",
    img: cardMatchday,
    alt: "Live sport on 12 large TVs at Plunkets Pub Dublin",
    href: "/events",
  },
  {
    badge: "2-FOR-1 MON–THU",
    badgeColor: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30",
    title: "Cocktails Worth Staying For",
    desc: "Around 20 cocktails from the bar — not an afterthought. Classics, signatures, late-night rounds. 2 cocktails for €18 all night Monday to Thursday, plus pitchers of beer (3.5 pints).",
    img: cardFood,
    alt: "Signature cocktails and drinks at Plunkets Pub Dublin",
    href: "/cocktails",
  },
] as const;

type Stat = { value: string; label: string; sub: string; star?: boolean; cta?: { label: string; href: string } };
const stats: Stat[] = [
  { value: "€12–€15", label: "per hour · Pool or Darts", sub: "Pool €12 · Darts €15" },
  { value: "23:30", label: "Open Until", sub: "Fri & Sat until 00:30" },
  { value: "4.9", label: "Google Rating", sub: "500+ reviews", star: true },
  { value: "Groups", label: "Always Welcome", sub: "Parties, socials, stags" },
];

function WhyPlunkets() {
  const shouldReduce = useReducedMotion();
  const { open: openModal } = useReservationModal();
  return (

    <section id="nights" className="relative px-5 py-16 md:px-8 md:py-36">
      <div className="mx-auto max-w-7xl">
        {/* Editorial header */}
        <motion.div
          initial={shouldReduce ? undefined : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 grid gap-10 border-t border-border pt-14 md:mb-20 md:grid-cols-12 md:gap-12 md:pt-20"
        >
          <div className="md:col-span-8">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">
              Why Plunkets
            </p>
            <h2 className="mt-6 font-display text-4xl font-bold leading-[0.95] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl">
              Three things done right.
              <span className="block text-muted-foreground/70">All under one roof.</span>
            </h2>
          </div>
          <div className="flex items-end md:col-span-4">
            <p className="text-base leading-relaxed text-muted-foreground md:text-[15px]">
              Most pubs in Dublin do one or two of these well. Plunkets does all three — and that's what keeps people coming back every week.
            </p>
          </div>
        </motion.div>

        {/* 3 Feature Cards */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {featureCards.map((card, i) => (
            <motion.div
              key={card.title}
              className="h-full"
              initial={shouldReduce ? undefined : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
            >
              <Link
                to={card.href}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={card.title}
              >
                {/* Background image */}
                <div className="relative aspect-[4/3] md:aspect-[3/4] lg:aspect-[4/5] overflow-hidden">
                  <img
                    src={card.img}
                    alt={card.alt}
                    width={800}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/40" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 lg:p-10">
                    <span className={`mb-4 inline-flex w-fit items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${card.badgeColor}`}>
                      {card.badge}
                    </span>
                    <h3 className="font-display text-xl font-bold leading-snug text-white md:text-2xl">
                      {card.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/85">
                      {card.desc}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-primary opacity-80 transition-all group-hover:opacity-100 group-hover:gap-3">
                      Explore <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* 4 Stats Row */}
        <motion.div
          initial={shouldReduce ? undefined : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
          className="mt-10 grid grid-cols-2 gap-px rounded-2xl border border-border bg-border md:mt-14 md:grid-cols-4"
        >
          {stats.map((s) => (
            <div key={s.label} className="bg-background px-5 py-8 md:px-6 md:py-10">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold text-primary md:text-5xl">{s.value}</span>
                {s.star && <Star size={20} className="text-primary fill-primary" />}
              </div>
              <p className="mt-1 text-sm font-semibold text-foreground">{s.label}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.sub}</p>
            </div>
          ))}
        </motion.div>

        {/* CTA below stats */}
        <div className="mt-8 flex justify-center md:mt-10">
          <button
            onClick={() => openModal({ bookingType: "food_and_drinks" })}
            className="btn-amber inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all hover:gap-3 sm:w-auto md:px-10 md:py-4 md:text-base"
          >
            Book a Table <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION: Pool & Darts Booking ---------- */
function PoolDartsBooking() {
  const prefersReducedMotion = useReducedMotion();
  const [space, setSpace] = useState<"pool" | "darts">("pool");
  const [submitted, setSubmitted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const lite = prefersReducedMotion || isMobile;
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", lite ? "0%" : "25%"]);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const bookingType = space === "pool" ? "Pool Table" : "Darts Oche";
    trackEvent("contact_form_submit", {
      form_name: "space_booking_request",
      booking_type: bookingType,
      party_size: Number(data.get("party") ?? 0) || undefined,
    });
    const subject = encodeURIComponent(`Booking request — ${bookingType} @ Plunkets`);
    const body = encodeURIComponent(
      `Space: ${bookingType}\n` +
      `Name: ${data.get("name")}\nPhone: ${data.get("phone")}\n` +
      `Date: ${data.get("date")}\nTime: ${data.get("time")}\n` +
      `Party size: ${data.get("party")}\nNotes: ${data.get("notes") || "-"}`
    );
    window.location.href = `mailto:bookings@plunkets.ie?subject=${subject}&body=${body}`;
    setSubmitted(true);
    form.reset();
  }


  const spaces = [
    { id: "pool" as const, label: "Pool Table", icon: Trophy, meta: "€12 / hr · up to 6 players", desc: "American-style tables, premium cues, by the hour." },
    { id: "darts" as const, label: "Darts Oche", icon: Beer, meta: "€15 / hr · up to 8 players", desc: "Tournament-grade boards, lit oches, scoring screens." },
  ];

  return (
    <section ref={sectionRef} id="book" className="relative overflow-hidden px-5 py-16 md:px-8 md:py-24">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img
          src={poolBookingParallaxBg.url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-[120%] w-full object-cover"
          style={{ objectPosition: "center" }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(0.55_0.12_60/0.08),transparent_60%)]" />
      <div className="relative z-10 mx-auto max-w-7xl">

      <div className="relative grid gap-12 lg:grid-cols-2 lg:gap-20">
        {/* Left: pitch + space picker */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          
          <h2 className="font-display text-4xl leading-[0.95] tracking-tight md:text-6xl">
            Book a <span className="text-gold-gradient">Pool Table</span> or <span className="text-gold-gradient">Darts Oche</span>.
          </h2>
          <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
            Skip the wait. Lock in a table for match night, your stag, a birthday or a casual catch-up — right in the heart of Dublin city centre.
          </p>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {spaces.map((s) => {
              const Icon = s.icon;
              const active = space === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSpace(s.id)}
                  aria-pressed={active}
                  className={`group relative rounded-2xl border p-5 text-left transition ${active ? "border-primary bg-primary/10" : "border-border bg-card/40 hover:border-primary/50"}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`grid h-10 w-10 place-items-center rounded-full border ${active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-display text-xl">{s.label}</p>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">{s.meta}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{s.desc}</p>
                </button>
              );
            })}
          </div>

          <ul className="mt-8 space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Same-day bookings welcome — we'll confirm within 24 hours.</li>
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Prefer to call? <a href="tel:+35315551234" className="text-primary hover:underline">+353 1 555 1234</a></li>
          </ul>
        </motion.div>

        {/* Right: form */}
        <motion.form
          onSubmit={onSubmit}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative rounded-3xl border border-border bg-card/60 p-6 backdrop-blur-md md:p-8"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name" name="name" required placeholder="Your name" />
            <Field label="Phone" name="phone" type="tel" required placeholder="+353 …" />
            <Field label="Date" name="date" type="date" required />
            <Field label="Time" name="time" type="time" required />
            <Field label="Party size" name="party" type="number" min={1} max={20} required placeholder="4" />
            <div className="sm:col-span-2">
              <Field label="Notes (optional)" name="notes" placeholder="Birthday, work social, tournament…" textarea />
            </div>
          </div>

          <button
            type="submit"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-sm font-semibold uppercase tracking-widest text-primary-foreground transition hover:opacity-90"
          >
            Request {space === "pool" ? "Pool Table" : "Darts Oche"}
            <ArrowRight className="h-4 w-4" />
          </button>

          {submitted && (
            <p className="mt-4 text-center text-sm text-primary">
              Your request is ready in your email client — hit send and we'll confirm shortly.
            </p>
          )}
          <p className="mt-3 text-center text-xs text-muted-foreground">
            No card needed. We hold your spot once confirmed by our team.
          </p>
        </motion.form>
      </div>

        {/* Gallery */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 grid grid-cols-2 gap-3 md:mt-24 md:grid-cols-4 md:gap-5"
        >
          {galleryImages.map((img, i) => (
            <figure
              key={img.src}
              className={`group relative overflow-hidden rounded-2xl border border-border bg-card/40 ${i === 0 ? "col-span-2 md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-[4/3]"}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                sizes="(max-width: 768px) 50vw, 25vw"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 md:px-5 md:pb-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/90 drop-shadow md:text-sm">
                  {img.caption}
                </p>
              </figcaption>
            </figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", placeholder, required, min, max, textarea }: {
  label: string; name: string; type?: string; placeholder?: string; required?: boolean; min?: number; max?: number; textarea?: boolean;
}) {
  const base = "mt-1.5 w-full rounded-lg border border-border bg-background/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea name={name} placeholder={placeholder} required={required} rows={3} className={base} />
      ) : (
        <input name={name} type={type} placeholder={placeholder} required={required} min={min} max={max} className={base} />
      )}
    </label>
  );
}

/* ---------- SECTION 4: Tonight Events (live from DB) ---------- */
function iconForTag(tag: string | null) {
  const t = (tag ?? "").toLowerCase();
  if (t.includes("sport") || t.includes("match")) return Trophy;
  if (t.includes("music") || t.includes("dj") || t.includes("trad")) return Music;
  if (t.includes("karaoke")) return Mic;
  if (t.includes("food") || t.includes("special")) return UtensilsCrossed;
  return Calendar;
}
function bookingTypeForTag(tag: string | null): BookingType {
  const t = (tag ?? "").toLowerCase();
  if (t.includes("sport") || t.includes("match")) return "sports_viewing";
  if (t.includes("group")) return "group_booking";
  return "food_and_drinks";
}
function formatEventTime(starts_at: string) {
  const d = new Date(starts_at);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000).toDateString() === d.toDateString();
  const time = d.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Tonight · ${time}`;
  if (tomorrow) return `Tomorrow · ${time}`;
  return `${d.toLocaleDateString("en-IE", { weekday: "short", day: "2-digit", month: "short" })} · ${time}`;
}

type DateFilter = "all" | "today" | "tomorrow" | "week";

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function TonightEvents() {
  const { open: openModal } = useReservationModal();
  const listFn = useServerFn(listPublishedEvents);
  const [filter, setFilter] = useState<DateFilter>("all");
  const sectionRef = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const lite = reduce || isMobile;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", lite ? "0%" : "25%"]);

  const { data: rawEvents = [] } = useQuery({
    queryKey: ["public-events"],
    queryFn: () => listFn({ data: { limit: 24 } }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    refetchInterval: 30_000,
  });

  const events = (() => {
    const sorted = [...rawEvents].sort(
      (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
    );
    if (filter === "all") return sorted.slice(0, 8);
    const now = new Date();
    const today = startOfDay(now);
    const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
    const dayAfter = new Date(today.getTime() + 48 * 3600 * 1000);
    const weekEnd = new Date(today.getTime() + 7 * 24 * 3600 * 1000);
    return sorted
      .filter((e) => {
        const d = new Date(e.starts_at);
        if (filter === "today") return d >= today && d < tomorrow;
        if (filter === "tomorrow") return d >= tomorrow && d < dayAfter;
        if (filter === "week") return d >= today && d < weekEnd;
        return true;
      })
      .slice(0, 8);
  })();

  const filters: { id: DateFilter; label: string }[] = [
    { id: "all", label: "All upcoming" },
    { id: "today", label: "Today" },
    { id: "tomorrow", label: "Tomorrow" },
    { id: "week", label: "This week" },
  ];

  return (
    <section id="tonight" ref={sectionRef} className="relative overflow-hidden px-5 py-16 md:px-8 md:py-24">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img
          src={tonightParallaxBg.url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-[120%] w-full object-cover"
          style={{ objectPosition: "center" }}
        />
        <div className="absolute inset-0 bg-background/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <SectionHeader title={<>Tonight at <span className="text-gold-gradient">Plunkets.</span></>} />

        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {filters.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={[
                  "rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-all",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card/30 text-muted-foreground hover:border-primary/40 hover:text-foreground",
                ].join(" ")}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {events.length === 0 ? (
          <p className="mt-14 text-center text-sm text-muted-foreground">
            {filter === "all"
              ? "New line-up dropping soon. Check back shortly."
              : `Nothing scheduled for ${filters.find((f) => f.id === filter)?.label.toLowerCase()}. Try another filter.`}
          </p>
        ) : (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {events.map((e, i) => {
              const Icon = iconForTag(e.tag);
              return (
                <motion.div key={e.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.5 }}
                  className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card/80 backdrop-blur transition hover:border-primary/50">
                  {e.banner_url && (
                    <Link to="/events/$slug" params={{ slug: e.slug ?? e.id }} className="relative block aspect-[16/10] overflow-hidden">
                      <img src={e.banner_url} alt={e.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                    </Link>
                  )}
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl transition-opacity group-hover:opacity-100" />
                  <div className="relative p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
                      <Icon size={14} /> {e.tag ?? "Event"}
                    </div>
                    <Link to="/events/$slug" params={{ slug: e.slug ?? e.id }} className="mt-4 block">
                      <h3 className="font-display text-2xl font-semibold leading-tight transition-colors group-hover:text-primary">{e.title}</h3>
                    </Link>
                    <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><Clock size={13} /> {formatEventTime(e.starts_at)}</p>
                  </div>
                  <div className="relative flex items-center justify-between gap-3 px-6 pb-6">
                    <Link to="/events/$slug" params={{ slug: e.slug ?? e.id }} className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                      Details <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <button onClick={() => openModal({ bookingType: bookingTypeForTag(e.tag) })} className="rounded-full border border-primary/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                      Reserve
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-card/60 px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary backdrop-blur transition hover:bg-primary hover:text-primary-foreground"
          >
            View all events <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 5: Group Booking ---------- */
const groupCategories = [
  { icon: "🎂", label: "Birthdays" },
  { icon: "💼", label: "Work Socials" },
  { icon: "🏆", label: "Match Day Groups" },
  { icon: "🎉", label: "Stag & Hen" },
  { icon: "🍻", label: "Casual Nights" },
  { icon: "🎓", label: "Student Nights" },
];

const groupPackages = [
  {
    title: "Match Day Package",
    subtitle: "The full match day experience.",
    features: [
      "Reserved table near the screens",
      "Screens dedicated to your match",
      "Pints or pitchers on arrival",
      "Sharing food platters",
    ],
    cta: "Book Match Day",
    bookingType: "group_booking" as BookingType,
    featured: false,
  },
  {
    title: "Pool & Cocktails",
    subtitle: "Make it a proper night out.",
    features: [
      "Pool table booking included",
      "Welcome cocktail round",
      "Sharing food for the group",
      "Dedicated group area",
    ],
    cta: "Book This Package",
    bookingType: "group_booking" as BookingType,
    featured: true,
  },
  {
    title: "Work Social Package",
    subtitle: "The easy yes for your next work night.",
    features: [
      "Reserved area for your team",
      "Pool tables for the group",
      "Flexible drinks packages",
      "Food platters included",
    ],
    cta: "Enquire Now",
    bookingType: "group_booking" as BookingType,
    featured: false,
  },
];

function GroupBooking() {
  const { open: openModal } = useReservationModal();
  const sectionRef = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const lite = reduce || isMobile;
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", lite ? "0%" : "25%"]);

  return (
    <section ref={sectionRef} id="groups" className="relative overflow-hidden px-5 py-16 md:px-8 md:py-32">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img
          src={groupNightsParallaxBg.url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-[120%] w-full object-cover"
          style={{ objectPosition: "center" }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </motion.div>
      <div className="absolute inset-0 -z-10 grain opacity-40" />


      <div className="relative z-10 mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-primary">
            Group Nights · Private Hire · Events
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-5 font-display text-4xl font-bold leading-[1.05] md:text-6xl lg:text-7xl"
          >
            Your Group's Night.
            <br />
            <span className="text-gold-gradient">Done Right.</span>
          </motion.h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm text-muted-foreground md:text-base">
            Pool tables, sport on every screen, cocktails at the bar, food platters — and staff who actually make group nights work. All in Dublin city centre. Right on Middle Abbey Street, minutes from O’Connell Street and the Luas.
          </p>
        </div>

        {/* Category pills */}
        <div className="mt-10 flex flex-wrap justify-center gap-2 md:gap-3">
          {groupCategories.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-2 text-xs font-medium text-foreground/90 backdrop-blur md:text-sm"
            >
              <span aria-hidden>{c.icon}</span>
              {c.label}
            </span>
          ))}
        </div>

        {/* Package cards */}
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {groupPackages.map((pkg, i) => (
            <motion.div
              key={pkg.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className={`relative flex flex-col rounded-2xl border p-6 backdrop-blur md:p-7 ${
                pkg.featured
                  ? "border-primary/60 bg-gradient-to-b from-primary/10 via-card/80 to-card/60 shadow-[0_0_60px_-20px_rgba(198,164,122,0.35)]"
                  : "border-border bg-card/70"
              }`}
            >
              {pkg.featured && (
                <span className="absolute right-5 top-5 rounded-full bg-primary/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-2xl font-bold leading-tight">{pkg.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{pkg.subtitle}</p>

              <ul className="mt-6 flex-1 space-y-3">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <span className="mt-0.5 text-primary">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => openModal({ bookingType: pkg.bookingType })}
                className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition ${
                  pkg.featured
                    ? "btn-amber"
                    : "btn-ghost-gold"
                }`}
              >
                {pkg.cta} <ArrowRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-8 rounded-2xl border border-border bg-card/50 p-6 text-center backdrop-blur md:p-8">
          <p className="text-sm text-muted-foreground md:text-base">
            Not sure which package fits? Give us a call or drop us a message — we'll sort it.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => openModal({ bookingType: "group_booking" })}
              className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
            >
              Plan a Group Night
            </button>
            <a
              href="tel:+35310000000"
              className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
            >
              <Phone size={16} /> Call Us Direct
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 6: Reviews ---------- */
const reviews = [
  { name: "Aoife K.", text: "Best spot in town to watch a match. Atmosphere was electric and the pints were perfect.", stars: 5 },
  { name: "Conor M.", text: "Booked a 20-person birthday upstairs. Food, music and craic — staff couldn't have been more sound.", stars: 5 },
  { name: "Niamh O.", text: "Cocktails are gorgeous, pool tables are sound and the live trad on Saturday made the night.", stars: 5 },
  { name: "Liam B.", text: "Right beside the Luas, easy to find. Loads of screens, never missed a kick of the game.", stars: 5 },
];

function Reviews() {
  return (
    <section className="relative px-5 py-16 md:px-8 md:py-40">
      <div className="mx-auto max-w-7xl">
        <SectionHeader eyebrow="06 · Word of Mouth" title={<>What Dublin <span className="text-gold-gradient">Says.</span></>} />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {reviews.map((r, i) => (
            <motion.div key={r.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="relative rounded-2xl border border-border bg-card/70 p-6 backdrop-blur transition hover:-translate-y-1 hover:border-primary/40">
              <div className="mb-3 flex gap-0.5 text-primary">
                {Array.from({ length: r.stars }).map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed">"{r.text}"</p>
              <div className="mt-5 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <span className="inline-block h-1 w-6 bg-primary" /> {r.name} · Google Review
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 7: Location ---------- */
function Location() {
  return (
    <section id="find" className="relative border-t border-border px-5 py-16 md:px-8 md:py-40">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHeader title={<>Right in the middle of the <span className="text-gold-gradient">action.</span></>} align="left" />
          <p className="mt-6 max-w-lg text-base text-muted-foreground md:text-lg">
            Find us on Middle Abbey Street, just minutes from O'Connell Street, the Luas and Dublin's busiest city-centre spots.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex items-center gap-3"><MapPin size={16} className="text-primary" /> Middle Abbey Street, Dublin 1, D01</li>
            <li className="flex items-center gap-3"><Clock size={16} className="text-primary" /> Open daily · 12:00 – 23:30 · Fri & Sat until 00:30</li>
            <li className="flex items-center gap-3"><Phone size={16} className="text-primary" /> +353 1 000 0000</li>
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="https://maps.google.com/?q=Middle+Abbey+Street+Dublin" target="_blank" rel="noreferrer" className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
              Get Directions <ArrowRight size={16} />
            </a>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
          className="relative aspect-square overflow-hidden rounded-3xl border border-border bg-card shadow-[var(--shadow-card)]">
          {/* Stylised animated map */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,oklch(0.32_0.07_155/0.4),transparent_70%)]" />
          <svg viewBox="0 0 400 400" className="absolute inset-0 h-full w-full">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="oklch(0.78 0.13 85 / 0.08)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="400" height="400" fill="url(#grid)" />
            <path d="M0 220 Q 100 200 200 220 T 400 200" stroke="oklch(0.78 0.13 85 / 0.4)" strokeWidth="2" fill="none" />
            <path d="M120 0 L 130 400" stroke="oklch(0.78 0.13 85 / 0.3)" strokeWidth="1.5" fill="none" />
            <path d="M280 0 L 270 400" stroke="oklch(0.78 0.13 85 / 0.3)" strokeWidth="1.5" fill="none" />
            <path d="M0 120 Q 200 130 400 110" stroke="oklch(0.32 0.07 155 / 0.5)" strokeWidth="1.5" fill="none" />
          </svg>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              <div className="absolute inset-0 -m-6 animate-ping rounded-full bg-primary/40" />
              <div className="absolute inset-0 -m-3 rounded-full bg-primary/30" />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-glow)]">
                <Beer size={22} />
              </div>
            </div>
            <div className="mt-4 whitespace-nowrap rounded-full border border-border bg-background/80 px-3 py-1 text-xs font-semibold backdrop-blur">
              Plunkets · Middle Abbey St
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}


/* ---------- SECTION: Menu & Cocktails Preview ---------- */
function MenuPreview() {
  const reduce = useReducedMotion();
  const { data: dbRows } = useQuery({
    queryKey: ["public-menu"],
    queryFn: () => listPublishedMenu(),
    staleTime: 60_000,
  });
  const items = mergeMenu(dbRows);
  const isHomeTag = (b?: string | null) => !!b && /home\s*view/i.test(b);
  // Prefer items explicitly tagged "home view" in admin. Fallback to varied top items.
  let highlights: typeof MENU = items.filter((it) => isHomeTag(it.badge)).slice(0, 8);
  if (highlights.length === 0) {
    const pickedSlugs = new Set<string>();
    const seenCategories = new Set<string>();
    const list: typeof MENU = [];
    for (const it of items) {
      if (list.length >= 4) break;
      if (seenCategories.has(it.category)) continue;
      list.push(it);
      pickedSlugs.add(it.slug);
      seenCategories.add(it.category);
    }
    for (const it of items) {
      if (list.length >= 4) break;
      if (pickedSlugs.has(it.slug)) continue;
      list.push(it);
      pickedSlugs.add(it.slug);
    }
    highlights = list;
  }



  return (
    <section id="menu" className="relative px-5 py-20 md:px-8 md:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={reduce ? undefined : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="grid gap-8 border-t border-border pt-14 md:grid-cols-12 md:gap-12"
        >
          <div className="md:col-span-8">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Cocktails & Menu</p>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight md:text-6xl">
              Proper drinks. <span className="text-gold-gradient">Proper food.</span>
            </h2>
          </div>
          <div className="flex items-end md:col-span-4">
            <p className="text-base text-muted-foreground">
              Around 20 cocktails, pints pulled right, pitchers of beer (3.5 pints) and a kitchen that keeps going late. 2 cocktails for €18 all night, Monday to Thursday.
            </p>
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 gap-4 md:mt-14 md:grid-cols-4 md:gap-5">
          {highlights.map((item, i) => (
            <motion.div
              key={item.slug}
              initial={reduce ? undefined : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
            >
              <Link
                to="/menu/$slug"
                params={{ slug: item.slug }}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card/60 backdrop-blur transition hover:border-primary/50"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 768px) 50vw, 25vw"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent" />
                  <span className="absolute right-2 top-2 rounded-full bg-primary/95 px-2 py-0.5 text-[11px] font-bold text-primary-foreground md:right-3 md:top-3 md:px-2.5 md:py-1 md:text-xs">
                    {item.price}
                  </span>
                  {item.badge && !isHomeTag(item.badge) && (
                    <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] truncate rounded-full border border-primary/40 bg-background/80 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary backdrop-blur md:bottom-3 md:left-3 md:px-2.5 md:py-1 md:text-[10px]">
                      {item.badge}
                    </span>
                  )}
                </div>
                <div className="p-4 md:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/80">{item.category}</p>
                  <h3 className="mt-1.5 font-display text-lg font-semibold leading-tight md:text-xl">{item.name}</h3>
                  <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground md:text-sm">{item.tagline}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary transition-all group-hover:gap-2">
                    View <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/cocktails"
            className="btn-amber inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-wider"
          >
            Cocktail Menu <ArrowRight size={16} />
          </Link>
          <Link
            to="/food-menu"
            className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-transparent px-8 py-4 text-sm font-semibold uppercase tracking-wider text-primary transition hover:bg-primary/10"
          >
            Food Menu <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION: Karaoke Terrace ---------- */
function KaraokeLounge() {
  const reduce = useReducedMotion();
  const { open: openModal } = useReservationModal();

  const features = [
    { icon: Mic, title: "Karaoke Terrace", desc: "Your crew, your playlist, your stage — proper mics and big screens." },
    { icon: Music, title: "10,000+ Song Library", desc: "Classics, chart hits, Irish anthems, guilty pleasures — all in one screen." },
    { icon: Beer, title: "Table Service", desc: "Cocktail rounds and pints delivered to the room so no one loses their turn." },
    { icon: Clock, title: "Open Late", desc: "Open until 23:30 daily, 00:30 Fri & Sat. Live music Thu–Sun (7 nights after the World Cup)." },
  ];

  const gallery = [
    {
      src: karaokeHomeWide,
      alt: "Karaoke Terrace neon stage and group seating at Plunkets Pub, Middle Abbey Street, Dublin city centre",
      title: "Karaoke Terrace in Dublin city centre — Plunkets Pub, Middle Abbey Street",
    },
    {
      src: karaokeHomeStage,
      alt: "Neon karaoke stage and microphone beside high tables at Plunkets karaoke bar in Dublin 1",
      title: "Karaoke stage at Plunkets, Dublin 1",
    },
    {
      src: karaokeHomeScreen,
      alt: "Karaoke screen with song lyrics and microphone on the Karaoke Terrace at Plunkets Pub Dublin",
      title: "10,000-song karaoke library at Plunkets Dublin",
    },
    {
      src: karaokeStageNeonHome,
      alt: "Karaoke Terrace booth seating near O'Connell Street at Plunkets, Dublin city centre karaoke pub",
      title: "Karaoke Terrace seating, Plunkets Dublin city centre",
    },
  ];


  return (
    <section
      id="karaoke"
      aria-labelledby="karaoke-home-title"
      className="relative overflow-hidden px-5 py-20 md:px-8 md:py-28"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": "https://plunkets.ie/#karaoke-terrace",
            name: "Karaoke Terrace Dublin City Centre",
            serviceType: "Karaoke venue hire",
            description:
              "Karaoke Terrace at Plunkets Pub on Middle Abbey Street, Dublin 1 — 10,000+ song library, big screens, table service and cocktails. Book a table for karaoke in Dublin city centre.",
            url: "https://plunkets.ie/karaoke-dublin",
            provider: { "@id": "https://plunkets.ie/#business" },
            areaServed: [
              { "@type": "City", name: "Dublin" },
              { "@type": "Place", name: "Dublin City Centre" },
              { "@type": "Place", name: "Dublin 1" },
            ],
          }),
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(0.55_0.12_60/0.10),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 lg:items-center">
          <motion.div
            initial={reduce ? undefined : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Karaoke Terrace · Dublin 1</p>
            <h2 id="karaoke-home-title" className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight md:text-6xl">
              Karaoke in Dublin City Centre. <span className="text-gold-gradient">Own the night.</span>
            </h2>
            <p className="mt-5 max-w-lg text-base text-muted-foreground md:text-lg">
              Plunkets' Karaoke Terrace is the best karaoke Dublin city centre has for big group nights — a proper stage, big sound, a
              10,000-song library and cocktails at your table. Perfect for birthdays, hens, work nights or a Thursday that
              just gets away from you.
            </p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {features.map((f) => (
                <li key={f.title} className="flex items-start gap-3 rounded-xl border border-border bg-card/50 p-4 backdrop-blur">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-primary/40 text-primary">
                    <f.icon size={16} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{f.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => {
                  trackEvent("booking_cta_click", { location: "home-karaoke-section", booking_type: "food_and_drinks" });
                  openModal({ bookingType: "food_and_drinks" });
                }}
                aria-label="Reserve a table for karaoke at Plunkets, Dublin city centre"
                className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                Reserve a Table for Karaoke <ArrowRight size={16} />
              </button>
              <button
                onClick={() => {
                  trackEvent("booking_cta_click", { location: "home-karaoke-section", booking_type: "group_booking" });
                  openModal({ bookingType: "group_booking" });
                }}
                className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                Group Booking (10+) <ArrowRight size={16} />
              </button>
              <Link
                to="/karaoke-dublin"
                className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                Explore the Karaoke Terrace <ArrowRight size={16} />
              </Link>
              <a href="tel:+35310000000" className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
                <Phone size={16} /> Call to Enquire
              </a>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Read more on our dedicated{" "}
              <Link to="/karaoke-dublin" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
                karaoke terrace in Dublin city centre
              </Link>{" "}
              page — song library, pricing and FAQs.
            </p>

          </motion.div>

          <motion.div
            initial={reduce ? undefined : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="grid grid-cols-2 gap-3 md:gap-4"
          >
            {gallery.map((g, i) => (
              <figure
                key={g.src}
                className={`group relative overflow-hidden rounded-2xl border border-border bg-card/40 ${i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  title={g.title}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 768px) 50vw, 25vw"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="sr-only">{g.alt}</figcaption>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
              </figure>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Shared header ---------- */
function SectionHeader({ eyebrow, title, align = "center" }: { eyebrow?: string; title: React.ReactNode; align?: "center" | "left" }) {
  return (
    <div className={align === "center" ? "text-center" : "text-left"}>
      {eyebrow && <p className="mb-4 text-xs uppercase tracking-[0.4em] text-primary">{eyebrow}</p>}
      <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
        className="font-display text-4xl font-bold leading-tight md:text-6xl lg:text-7xl">
        {title}
      </motion.h2>
    </div>
  );
}
